import React, { memo, useEffect, useMemo, useState } from 'react';
import { AIModel } from '@/types';
import { useAppStore } from '@/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Bot,
  Server,
  Cloud,
  Laptop,
  Sparkles,
  Check,
  ChevronDown,
  FlaskConical,
  Monitor,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import Gemma4OnnxConsentDialog from '@/components/models/Gemma4OnnxConsentDialog';
import {
  appendGemma4OnnxModel,
  isGemma4OnnxModelId,
  loadGemma4OnnxAcknowledged,
} from '@/lib/gemma4Onnx';
import {
  DEFAULT_PROVIDER_ORDER,
  filterEnabledModels,
  getModelProviderKey,
  isProviderEnabled,
  loadModelSettings,
} from '@/lib/modelSettings';

interface ModelSelectorProps {
  models: AIModel[];
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
}

const mergeProviderOrder = (providerOrder: string[]) => {
  const unique = providerOrder.filter((value, index) => providerOrder.indexOf(value) === index);
  return [...unique, ...DEFAULT_PROVIDER_ORDER.filter((key) => !unique.includes(key))];
};

const isOnDeviceModel = (model: AIModel) => {
  if (model.provider === 'local' || model.provider === 'transformers') {
    return true;
  }

  return model.provider === 'ollama' && model.source === 'local';
};

const getDefaultProviderMeta = (key: string) => {
  switch (key) {
    case 'openai':
      return { name: 'OpenAI', type: 'cloud' as const };
    case 'gemini':
      return { name: 'Gemini', type: 'cloud' as const };
    case 'deepseek':
      return { name: 'DeepSeek', type: 'cloud' as const };
    case 'cohere':
      return { name: 'Cohere', type: 'cloud' as const };
    case 'maplept':
      return { name: 'MaplePT', type: 'cloud' as const };
    case 'ollama_remote':
      return { name: 'Ollama', type: 'cloud' as const };
    case 'custom':
      return { name: 'Custom', type: 'cloud' as const };
    case 'local':
      return { name: 'MapleOS', type: 'on-device' as const };
    case 'ollama_local':
      return { name: 'Local Ollama', type: 'on-device' as const };
    case 'transformers':
      return { name: 'Google', type: 'on-device' as const };
    default:
      return {
        name: key
          .split(/[_-]/g)
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' '),
        type: 'cloud' as const,
      };
  }
};

const ModelSelector: React.FC<ModelSelectorProps> = memo(({ models: availableModelsProp, selectedModelId, onModelChange }) => {
  const { user } = useAppStore();
  const isMobile = useIsMobile();
  const [providerOrderKeys, setProviderOrderKeys] = useState<string[]>(DEFAULT_PROVIDER_ORDER);
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingGemmaModelId, setPendingGemmaModelId] = useState<string | null>(null);
  const [gemmaAcknowledged, setGemmaAcknowledged] = useState(false);

  useEffect(() => {
    const settings = loadModelSettings();
    setProviderOrderKeys(mergeProviderOrder(settings.providerOrder || DEFAULT_PROVIDER_ORDER));
    void loadGemma4OnnxAcknowledged().then(setGemmaAcknowledged);
  }, []);

  const availableModels = useMemo(() => {
    const withOnDevice = filterEnabledModels(appendGemma4OnnxModel(Array.isArray(availableModelsProp) ? availableModelsProp : []));

    if (withOnDevice.length === 0) return [];
    if (!user) {
      return withOnDevice.filter(
        (model) => model.tier === 'free' || isOnDeviceModel(model) || model.provider === 'ollama'
      );
    }

    return withOnDevice;
  }, [availableModelsProp, user]);

  useEffect(() => {
    const selectedAvailable = availableModels.some((model) => model.id === selectedModelId);
    if (availableModels.length > 0 && (!selectedAvailable || !selectedModelId)) {
      onModelChange(availableModels[0].id);
    } else if (availableModels.length === 0 && selectedModelId) {
      onModelChange('');
    }
  }, [availableModels, onModelChange, selectedModelId]);

  const handleModelSelection = (modelId: string) => {
    if (isGemma4OnnxModelId(modelId) && !gemmaAcknowledged) {
      setPendingGemmaModelId(modelId);
      setConsentOpen(true);
      return;
    }

    onModelChange(modelId);
  };

  const handleConsentConfirmed = () => {
    setGemmaAcknowledged(true);
    if (pendingGemmaModelId) {
      onModelChange(pendingGemmaModelId);
    }
  };

  const selectedModelData = useMemo(
    () => availableModels.find((model) => model.id === selectedModelId),
    [availableModels, selectedModelId]
  );

  const getProviderIcon = (model: AIModel) => {
    if (model.logo_url) {
      return <AvatarImage src={model.logo_url} alt={model.name} />;
    }

    if (model.provider === 'ollama' && model.source === 'remote') {
      return <Cloud className="h-4 w-4 text-blue-600" />;
    }

    if (model.provider === 'ollama' && model.source === 'local') {
      return <Laptop className="h-4 w-4 text-emerald-600" />;
    }

    if (model.provider === 'local') {
      return <Laptop className="h-4 w-4 text-emerald-600" />;
    }

    if (model.provider === 'transformers') {
      return <FlaskConical className="h-4 w-4 text-amber-500" />;
    }

    switch (model.provider) {
      case 'openai':
      case 'gemini':
      case 'deepseek':
      case 'cohere':
      case 'maplept':
        return <Bot className="h-4 w-4 text-muted-foreground" />;
      case 'custom':
        return <Sparkles className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Server className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getProviderTriggerIcon = (model: AIModel) => {
    if (model.logo_url) {
      return (
        <Avatar className="h-5 w-5 bg-muted">
          <AvatarImage src={model.logo_url} alt={model.name} />
          <AvatarFallback>{model.name.slice(0, 2)}</AvatarFallback>
        </Avatar>
      );
    }

    if (model.provider === 'ollama' && model.source === 'remote') {
      return <Cloud className="h-5 w-5 text-blue-600" />;
    }

    if (model.provider === 'ollama' && model.source === 'local') {
      return <Laptop className="h-5 w-5 text-emerald-600" />;
    }

    if (model.provider === 'local') {
      return <Laptop className="h-5 w-5 text-emerald-600" />;
    }

    if (model.provider === 'transformers') {
      return <FlaskConical className="h-5 w-5 text-amber-500" />;
    }

    switch (model.provider) {
      case 'openai':
      case 'gemini':
      case 'deepseek':
      case 'cohere':
      case 'maplept':
        return <Bot className="h-5 w-5 text-muted-foreground" />;
      case 'custom':
        return <Sparkles className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Server className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const providerGroups = useMemo(() => {
    const groups = new Map<string, { models: AIModel[]; name: string; type: 'cloud' | 'on-device' }>();

    availableModels.forEach((model) => {
      const key = getModelProviderKey(model);
      if (!groups.has(key)) {
        groups.set(key, {
          models: [],
          ...getDefaultProviderMeta(key),
        });
      }
      groups.get(key)!.models.push(model);
    });

    const mergedOrder = mergeProviderOrder(providerOrderKeys);
    const orderedGroups = mergedOrder
      .filter((key) => groups.has(key) && isProviderEnabled(key))
      .map((key) => {
        const group = groups.get(key)!;
        const providerModel = availableModels.find((model) => getModelProviderKey(model) === key);
        return {
          key,
          name: group.name,
          type: group.type,
          models: [...group.models].sort((a, b) => a.name.localeCompare(b.name)),
          logo_url: providerModel?.logo_url,
        };
      });

    const unorderedGroups = Array.from(groups.entries())
      .filter(([key]) => !mergedOrder.includes(key) && isProviderEnabled(key))
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([key, group]) => {
        const providerModel = availableModels.find((model) => getModelProviderKey(model) === key);
        return {
          key,
          name: group.name,
          type: group.type,
          models: [...group.models].sort((aOrder, bOrder) => aOrder.name.localeCompare(bOrder.name)),
          logo_url: providerModel?.logo_url,
        };
      });

    return {
      groupedModels: [...orderedGroups, ...unorderedGroups],
      hasCloudModels: [...orderedGroups, ...unorderedGroups].some((group) => group.type === 'cloud'),
      hasOnDeviceModels: [...orderedGroups, ...unorderedGroups].some((group) => group.type === 'on-device'),
    };
  }, [availableModels, providerOrderKeys]);

  const triggerWidthClass = isMobile ? 'w-full' : 'w-[220px]';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={`${triggerWidthClass} justify-between font-normal`} disabled={availableModels.length === 0}>
            <div className="flex items-center gap-2 min-w-0">
              {selectedModelData ? (
                <>
                  {getProviderTriggerIcon(selectedModelData)}
                  <span className="truncate">{selectedModelData.name}</span>
                </>
              ) : (
                <>
                  <Monitor className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">Select a model</span>
                </>
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[280px] max-h-[60vh] overflow-y-auto">
          {availableModels.length > 0 ? (
            <>
              {providerGroups.hasCloudModels && (
                <>
                  <DropdownMenuLabel>Cloud</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {providerGroups.groupedModels
                      .filter((group) => group.type === 'cloud')
                      .map((group, index, groups) => (
                        <React.Fragment key={group.key}>
                          {group.models.length > 1 ? (
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <div className="flex items-center gap-2 min-w-0">
                                  {group.logo_url ? (
                                    <Avatar className="h-5 w-5 bg-muted">
                                      <AvatarImage src={group.logo_url} alt={group.name} />
                                      <AvatarFallback>{group.name.slice(0, 2)}</AvatarFallback>
                                    </Avatar>
                                  ) : (
                                    <Avatar className="h-5 w-5 bg-muted">
                                      <AvatarFallback>
                                        {group.models[0] ? getProviderIcon(group.models[0]) : <Server className="h-4 w-4 text-muted-foreground" />}
                                      </AvatarFallback>
                                    </Avatar>
                                  )}
                                  <span className="truncate">{group.name}</span>
                                </div>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="w-[260px]">
                                {group.models.map((model) => (
                                  <DropdownMenuItem key={model.id} onSelect={() => handleModelSelection(model.id)}>
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <Avatar className="h-5 w-5 bg-muted">
                                        {getProviderIcon(model)}
                                        <AvatarFallback>{model.name.slice(0, 2)}</AvatarFallback>
                                      </Avatar>
                                      <span className="truncate">{model.name}</span>
                                    </div>
                                    {selectedModelId === model.id ? <Check className="h-4 w-4" /> : null}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          ) : (
                            <DropdownMenuItem onSelect={() => handleModelSelection(group.models[0].id)}>
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Avatar className="h-5 w-5 bg-muted">
                                  {getProviderIcon(group.models[0])}
                                  <AvatarFallback>{group.models[0].name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <span className="truncate">{group.models[0].name}</span>
                              </div>
                              {selectedModelId === group.models[0].id ? <Check className="h-4 w-4" /> : null}
                            </DropdownMenuItem>
                          )}
                          {index < groups.length - 1 ? <DropdownMenuSeparator /> : null}
                        </React.Fragment>
                      ))}
                  </DropdownMenuGroup>
                </>
              )}

              {providerGroups.hasCloudModels && providerGroups.hasOnDeviceModels ? <DropdownMenuSeparator /> : null}

              {providerGroups.hasOnDeviceModels && (
                <>
                  <DropdownMenuLabel>On-device</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {providerGroups.groupedModels
                      .filter((group) => group.type === 'on-device')
                      .map((group, index, groups) => (
                        <React.Fragment key={group.key}>
                          {group.models.length > 1 ? (
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <div className="flex items-center gap-2 min-w-0">
                                  {group.logo_url ? (
                                    <Avatar className="h-5 w-5 bg-muted">
                                      <AvatarImage src={group.logo_url} alt={group.name} />
                                      <AvatarFallback>{group.name.slice(0, 2)}</AvatarFallback>
                                    </Avatar>
                                  ) : (
                                    <Avatar className="h-5 w-5 bg-muted">
                                      <AvatarFallback>
                                        {group.models[0] ? getProviderIcon(group.models[0]) : <Server className="h-4 w-4 text-muted-foreground" />}
                                      </AvatarFallback>
                                    </Avatar>
                                  )}
                                  <span className="truncate">{group.name}</span>
                                </div>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="w-[260px]">
                                {group.models.map((model) => (
                                  <DropdownMenuItem key={model.id} onSelect={() => handleModelSelection(model.id)}>
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <Avatar className="h-5 w-5 bg-muted">
                                        {getProviderIcon(model)}
                                        <AvatarFallback>{model.name.slice(0, 2)}</AvatarFallback>
                                      </Avatar>
                                      <span className="truncate">{model.name}</span>
                                      {isGemma4OnnxModelId(model.id) ? <Badge variant="outline">ONNX</Badge> : null}
                                    </div>
                                    {selectedModelId === model.id ? <Check className="h-4 w-4" /> : null}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          ) : (
                            <DropdownMenuItem onSelect={() => handleModelSelection(group.models[0].id)}>
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Avatar className="h-5 w-5 bg-muted">
                                  {getProviderIcon(group.models[0])}
                                  <AvatarFallback>{group.models[0].name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <span className="truncate">{group.models[0].name}</span>
                                {isGemma4OnnxModelId(group.models[0].id) ? <Badge variant="outline">ONNX</Badge> : null}
                              </div>
                              {selectedModelId === group.models[0].id ? <Check className="h-4 w-4" /> : null}
                            </DropdownMenuItem>
                          )}
                          {index < groups.length - 1 ? <DropdownMenuSeparator /> : null}
                        </React.Fragment>
                      ))}
                  </DropdownMenuGroup>
                </>
              )}
            </>
          ) : (
            <DropdownMenuItem disabled>No models available</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Gemma4OnnxConsentDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onConfirmed={handleConsentConfirmed}
      />

    </>
  );
});

ModelSelector.displayName = 'ModelSelector';

export default ModelSelector;